import {
  ArrowDown01Icon,
  CancelCircleIcon,
  CheckmarkCircle01Icon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react-native';
import React, { useCallback, useState } from 'react';
import {
  Pressable,
  StyleSheet,
  View,
  type LayoutChangeEvent,
  type StyleProp,
  type ViewStyle,
} from 'react-native';
import { Menu } from 'react-native-paper';
import { Colors } from '../constants/Colors';
import { menuContentStyle } from '../constants/Styles';
import { useDynamicStyles } from '../hooks/useDynamicStyles';
import { isIos, type WithResponsive } from '../utils/responsivity';
import { Button } from './Button';
import { Loader } from './Loader';
import { TextReg } from './StyledText';

type Props<T extends string | number> = {
  label?: string;
  placeholder?: string;
  options: Array<{ label: string; value: T }>;
  value?: T;
  onChange: (value: T) => void;
  onClear?: () => void;
  buttonStyle?: StyleProp<ViewStyle>;
  menuStyle?: StyleProp<ViewStyle>;
  contentStyle?: StyleProp<ViewStyle>;
  notClearable?: boolean;
  variant?: 'slim' | 'normal';
  todo?: boolean;
  done?: boolean;
  showLoadingState?: boolean;
  error?: boolean;
};

type MenuItemProps<T extends string | number> = {
  option: { label: string; value: T };
  isSelected: boolean;
  onSelect: () => void;
};

function DropdownMenuItem<T extends string | number>({
  option,
  isSelected,
  onSelect,
}: MenuItemProps<T>) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Pressable
      onPress={onSelect}
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
      style={[
        menuItemStyles.menuItem,
        isHovered && menuItemStyles.menuItemHovered,
      ]}
    >
      <View style={menuItemStyles.menuItemContent}>
        <TextReg
          numberOfLines={1}
          ellipsizeMode="middle"
          style={isSelected ? menuItemStyles.selectedText : undefined}
        >
          {option.label}
        </TextReg>
        {isSelected && (
          <HugeiconsIcon
            icon={CheckmarkCircle01Icon}
            color={Colors.purple1}
            size={18}
          />
        )}
      </View>
    </Pressable>
  );
}

const menuItemStyles = StyleSheet.create({
  menuItem: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    cursor: 'pointer',
  },
  menuItemHovered: {
    backgroundColor: Colors.opacity10,
  },
  menuItemContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 8,
  },
  selectedText: {
    color: Colors.purple1,
  },
});

export function Dropdown<T extends string | number>({
  label,
  placeholder,
  options,
  value,
  onChange,
  onClear,
  buttonStyle,
  notClearable,
  variant = 'normal',
  todo,
  done,
  menuStyle,
  contentStyle,
  showLoadingState,
  error,
}: Props<T>) {
  const [visible, setVisible] = useState(false);
  const [buttonWidth, setButtonWidth] = useState<number | undefined>(undefined);
  const selectedLabel = options.find((option) => option.value === value)?.label;
  const canClearSelection = Boolean(selectedLabel && !notClearable && onClear);
  const styles = useDynamicStyles(createStyles<T>, {
    variant,
    done,
    error,
  });

  const closeMenu = () => {
    setVisible(false);
  };

  const selectItem = (value: T) => {
    onChange(value);
    closeMenu();
  };

  const handlePress = () => {
    // If a clear handler is provided and we can clear, clear the selection; otherwise open the menu
    if (canClearSelection && onClear) {
      onClear();
      return;
    }

    if (options.length) {
      setVisible(true);
    }
  };

  const handleLayout = useCallback((event: LayoutChangeEvent) => {
    setButtonWidth(event.nativeEvent.layout.width);
  }, []);

  const dropdownContent = (
    <View style={menuStyle} onLayout={handleLayout}>
      <Menu
        visible={visible}
        onDismiss={closeMenu}
        contentStyle={[
          menuContentStyle,
          buttonWidth ? { minWidth: buttonWidth } : undefined,
          contentStyle,
        ]}
        anchor={
          <Button
            mode="contained"
            onPress={handlePress}
            style={[buttonStyle, error && styles.errorBorder]}
            contentStyle={styles.buttonContent}
            labelStyle={styles.buttonLabel}
            buttonColor={isIos ? Colors.opacity025 : Colors.opacity05}
            size="medium"
            disabled={options.length == 0 && showLoadingState}
          >
            <TextReg style={styles.hiddenText}>
              {selectedLabel ?? placeholder}
            </TextReg>
          </Button>
        }
        anchorPosition="bottom"
      >
        {options.map((option) => (
          <DropdownMenuItem
            key={option.value.toString()}
            option={option}
            isSelected={option.value === value}
            onSelect={() => selectItem(option.value)}
          />
        ))}
      </Menu>
      <View style={styles.absoluteContent}>
        {todo && <View style={styles.todo} />}
        {selectedLabel ? (
          <TextReg numberOfLines={1} style={styles.text}>
            {selectedLabel}
          </TextReg>
        ) : (
          <TextReg numberOfLines={1} style={styles.placeholder}>
            {placeholder}
          </TextReg>
        )}
        {options.length ? (
          <HugeiconsIcon
            icon={canClearSelection ? CancelCircleIcon : ArrowDown01Icon}
            color={Colors.text}
          />
        ) : (
          showLoadingState && <Loader style={{ alignItems: 'flex-end' }} />
        )}
      </View>
    </View>
  );

  if (label) {
    return (
      <View style={labelContainerStyles.container}>
        <TextReg>{label}</TextReg>
        {dropdownContent}
      </View>
    );
  }

  return dropdownContent;
}

const labelContainerStyles = StyleSheet.create({
  container: {
    gap: 4,
  },
});

function createStyles<T extends string | number>({
  variant: _variant,
  done,
  error,
}: WithResponsive<Props<T>>) {
  return StyleSheet.create({
    buttonLabel: {
      paddingRight: 16,
      opacity: 0,
    },
    absoluteContent: {
      position: 'absolute',
      pointerEvents: 'none',
      alignItems: 'center',
      flexDirection: 'row',
      justifyContent: 'space-between',
      paddingHorizontal: 16,
      height: 40,
      width: '100%',
      gap: 10,
    },
    content: {
      height: 40,
    },
    buttonContent: {
      height: 40,
    },
    label: {
      width: '100%',
      marginLeft: 16,
      marginRight: 16,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      fontSize: 16,
    },
    text: {
      fontSize: 16,
    },
    placeholder: {
      fontSize: 16,
      color: Colors.placeholderText,
    },
    hiddenText: {
      fontSize: 16,
      paddingRight: 4,
    },
    iconContainer: {
      paddingRight: 16,
    },
    todo: {
      borderRadius: 6,
      width: 12,
      height: 12,
      backgroundColor: done ? Colors.green0 : Colors.opacity20,
    },
    errorBorder: {
      borderWidth: error ? 1 : 0,
      borderColor: Colors.orange0,
    },
  });
}
